import React, { useState, useEffect } from "react";
import { Loader, Sheet, SheetContent } from "../../../components";
import * as isEmpty from "lodash.isempty";
import {
  Typography,
  Button,
  CircularProgress,
  Box,
  Paper,
  Grid,
  Divider
} from "@material-ui/core";
import { useHistory } from "react-router";
import { ROUTE_PATHS } from "../../../constants";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/react-hooks";
import { POST_ADD_VAX_YES_CERTIFICATE } from "../../../queries";
import { useSnackbar } from "notistack";
import { useConsumerAuth } from "../../../hooks";
import {
  VaxYesRegisterStepPhotoId,
  VaxYesRegisterStepIdScan,
  VaxYesRegisterStepVaccineCard,
  VaxYesRegisterStepVaccineType,
  VaxYesRegisterStepContact
} from "../Register";
import TagManager from "react-gtm-module";

const REGISTER_SCREENS = [
  "scan-id",
  "vaccine-card",
  "vaccine-type",
  "contact",
  "photo-id"
];

const ProgressHeader = ({ currentScreenIndex, onBack, totalSteps }) => {
  const { t } = useTranslation();

  return (
    <Box mb={2}>
      <Grid container spacing={1} alignItems="center" justify="space-between">
        <Grid item>
          <Button onClick={onBack} disabled={currentScreenIndex === 0}>
            {t("vax_yes.vax_yes_register_step.button_back")}
          </Button>
        </Grid>
        <Typography>
          {t("vax_yes.vax_yes_register_step.progress", {
            current: currentScreenIndex + 1,
            total: totalSteps
          })}
        </Typography>
      </Grid>
      <Divider />
    </Box>
  );
};

const VaxYesRegisterStep = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { phone_number } = useConsumerAuth();

  const [currentScreenIndex, setCurrentScreenIndex] = useState(0);
  const [drawerClearance, setDrawerClearance] = useState(0);
  const [registerData, setRegisterData] = useState({});
  const [loading, setLoading] = useState(false);

  const [
    postAddVaxYesCertificate,
    { loading: submittingCertificate }
  ] = useMutation(POST_ADD_VAX_YES_CERTIFICATE, {
    onCompleted: ({ certificate }) => {
      try {
        const { status, show_payment_view } = certificate;

        if (status === "success") {
          if (show_payment_view) {
            return history.push({
              pathname: `${ROUTE_PATHS.VAXYES.REGISTER_BASE}/payment`,
              state: registerData
            });
          } else {
            return history.push({
              pathname: `${ROUTE_PATHS.VAXYES.REGISTER_BASE}/success`,
              state: registerData
            });
          }
        }
      } catch (error) {
        console.log(error);
      }

      return enqueueSnackbar(t("vax_yes.vax_yes_register_step.error_api"), {
        variant: "error"
      });
    }
  });

  useEffect(() => {
    setLoading(false);

    TagManager.dataLayer({
      dataLayer: {
        event: "step",
        page: { path: `${location.pathname}?step=${currentScreenIndex + 1}` }
      },
    });
    
  }, [currentScreenIndex]);

  useEffect(() => {
    if (isEmpty(registerData)) return;

    const nextScreenIndex = currentScreenIndex + 1;

    if (REGISTER_SCREENS[nextScreenIndex]) {
      setCurrentScreenIndex(nextScreenIndex);
    } else {
      setLoading(false);

      const group_code = sessionStorage.getItem("Group_Code");

      postAddVaxYesCertificate({
        variables: {
          body: {
            ...registerData,
            phone_number,
            first_vax_dt: registerData.vax_1_date || "",
            second_vax_dt: registerData.vax_2_date || "",
            group_code: group_code || ""
          }
        }
      });
    }
  }, [registerData]);

  const updateRegisterData = data => {
    setLoading(true);

    setRegisterData(registerData => ({
      ...registerData,
      ...data
    }));
  };

  const handleBack = () => {
    setCurrentScreenIndex(currentScreenIndex => currentScreenIndex - 1);
  };

  if (submittingCertificate) return <Loader />;

  return (
    <Sheet
      data-cy="vax-yes-register-register-step"
      style={{ paddingBottom: `${drawerClearance}px` }}
    >
      <SheetContent>
        <Box mb={2}>
          <Paper>
            <Box p={2}>
              {loading ? (
                <Box textAlign="center">
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <ProgressHeader
                    currentScreenIndex={currentScreenIndex}
                    onBack={handleBack}
                    totalSteps={REGISTER_SCREENS.length}
                  />
                  {{
                    "scan-id": (
                      <VaxYesRegisterStepIdScan
                        registerData={registerData}
                        setDrawerClearance={setDrawerClearance}
                        onNext={updateRegisterData}
                      />
                    ),
                    "photo-id": (
                      <VaxYesRegisterStepPhotoId
                        initialIdPhoto={registerData.id_image || ""}
                        setDrawerClearance={setDrawerClearance}
                        onNext={updateRegisterData}
                      />
                    ),
                    "vaccine-card": (
                      <VaxYesRegisterStepVaccineCard
                        initialVaccineCardPhoto={registerData.vax_image || ""}
                        setDrawerClearance={setDrawerClearance}
                        onNext={updateRegisterData}
                      />
                    ),
                    "vaccine-type": (
                      <VaxYesRegisterStepVaccineType
                        registerData={registerData}
                        setDrawerClearance={setDrawerClearance}
                        onNext={updateRegisterData}
                      />
                    ),
                    contact: (
                      <VaxYesRegisterStepContact
                        registerData={registerData}
                        setDrawerClearance={setDrawerClearance}
                        onNext={updateRegisterData}
                      />
                    )
                  }[REGISTER_SCREENS[currentScreenIndex]] || (
                    <Typography color="error">
                      {t("vax_yes.vax_yes_register_step.invalid_page")}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Paper>
        </Box>
      </SheetContent>
    </Sheet>
  );
};

export { VaxYesRegisterStep };
